// src/client/hooks/useSearch.ts
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { SaleItemInterface } from "../types/types";

export const useSearch = (
  items: SaleItemInterface[],
  onRemoteSearch?: (query: string) => Promise<SaleItemInterface[] | null>,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get("search") || "";

  const [query, setQueryState] = useState(urlQuery);
  const [remoteResults, setRemoteResults] = useState<
    SaleItemInterface[] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (urlQuery !== query) {
      setQueryState(urlQuery);
    }
  }, [urlQuery]);

  const filteredItems = useMemo(() => {
    if (!query.trim()) return items;
    if (remoteResults !== null) return remoteResults;

    const lowerQuery = query.toLowerCase();
    return items.filter(
      (item) =>
        item.name?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery) ||
        item.barCode?.toLowerCase().includes(lowerQuery) ||
        item.id?.toString().includes(lowerQuery),
    );
  }, [items, query, remoteResults]);

  const handleSearch = useCallback(
    (value: string) => {
      setQueryState(value);

      if (value) {
        setSearchParams({ search: value });
      } else {
        setSearchParams({});
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(async () => {
        if (onRemoteSearch && value.trim()) {
          setLoading(true);
          try {
            const results = await onRemoteSearch(value);
            setRemoteResults(results);
          } catch (error) {
            console.error("Remote search error:", error);
            setRemoteResults(null);
          } finally {
            setLoading(false);
          }
        } else {
          setRemoteResults(null);
        }
      }, 500);
    },
    [onRemoteSearch, setSearchParams],
  );

  const clearSearch = useCallback(() => {
    setQueryState("");
    setSearchParams({});
    setRemoteResults(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [setSearchParams]);

  return {
    query,
    setQuery: handleSearch,
    filteredItems,
    loading,
    clearSearch,
  };
};
